import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { EASE } from "./Reveal";
import SocialLinks from "./SocialLinks";

const LINES = ["Lawyers are trained", "for the ten percent.", "I build for", "the ninety."];

const MaskedLine = ({ text, index }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.35 + index * 0.13, ease: EASE }}
    >
      {text}
    </motion.span>
  </span>
);

const Hero = ({ onNavigate }) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const meshY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const rotateX = useSpring(tiltX, { stiffness: 120, damping: 18 });
  const rotateY = useSpring(tiltY, { stiffness: 120, damping: 18 });

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    tiltY.set(((e.clientX - rect.left) / rect.width - 0.5) * 8);
    tiltX.set(-((e.clientY - rect.top) / rect.height - 0.5) * 8);
  };
  const onMouseLeave = () => { tiltX.set(0); tiltY.set(0); };

  return (
    <div ref={sectionRef} className="hero-mesh" data-testid="hero-section">
      <motion.div className="hero-blob" style={{ width: 420, height: 420, top: -120, left: "6%", background: "rgba(168,195,222,0.55)", y: meshY }}
        animate={{ x: [0, 40, 0], y: [0, 24, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="hero-blob" style={{ width: 380, height: 380, top: -80, right: "4%", background: "rgba(91,123,163,0.35)" }}
        animate={{ x: [0, -36, 0], y: [0, 30, 0] }} transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }} />
      <div className="hero-noise" />

      <div className="wrap" style={{ position: "relative" }}>
        <nav className="site-nav" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 32, padding: "24px 0" }}>
          <motion.a href="#" onClick={(e) => { e.preventDefault(); onNavigate(null); }} style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}
            initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}
            data-testid="nav-logo">
            <img src="/assets/rv-monogram-ink.png" alt="RV — Rajesh Vaishnav" className="nav-logo" />
          </motion.a>
          <motion.div className="nav-links" style={{ display: "flex", alignItems: "center", gap: 28 }}
            initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE }}>
            {[["story", "Story"], ["work", "Work"], ["writing", "Writing"], ["contact", "Contact"]].map(([id, label]) => (
              <a key={id} href={`#${id}`} className="nav-link" data-testid={`nav-${id}`}
                onClick={(e) => { e.preventDefault(); onNavigate(`#${id}`); }}>
                {label}
              </a>
            ))}
            <span style={{ width: 1, height: 18, background: "var(--hairline)" }} className="nav-divider" />
            <SocialLinks size={15} testPrefix="header-social" />
          </motion.div>
        </nav>

        <div className="hero-body" style={{ display: "flex", alignItems: "center", gap: 72, flexWrap: "wrap", padding: "64px 0 96px" }}>
          <div style={{ flex: "1 1 540px", minWidth: 0 }}>
            <motion.div
              style={{ fontSize: 18, letterSpacing: 3, lineHeight: 1.2, textTransform: "uppercase", fontWeight: 400, color: "var(--ink)", marginBottom: 20 }}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
              data-testid="hero-name">
              <b>Rajesh Vaishnav</b>
            </motion.div>
            <h1 style={{ margin: 0, fontSize: "var(--display-xxl-size)", lineHeight: "var(--display-xxl-lh)", letterSpacing: "var(--display-xxl-ls)", fontWeight: "var(--weight-thin)", color: "var(--ink)", textWrap: "pretty" }}
              data-testid="hero-headline">
              {LINES.map((l, i) => <MaskedLine key={i} text={l} index={i} />)}
            </h1>
            <motion.p style={{ margin: "24px 0 0", fontSize: "var(--heading-md-size)", lineHeight: "var(--heading-md-lh)", letterSpacing: "var(--heading-md-ls)", fontWeight: "var(--weight-thin)", color: "var(--ink-mute)" }}
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
              data-testid="hero-tagline">
              Advocate · Founder · ex-SeedLegals
            </motion.p>
            <motion.div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 36 }}
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.15, ease: EASE }}>
              <a href="#work" className="link-primary" style={{ fontSize: "var(--body-lg-size)", fontWeight: 400 }}
                onClick={(e) => { e.preventDefault(); onNavigate("#work"); }} data-testid="hero-cta">
                See the work <span className="arrow">→</span>
              </a>
            </motion.div>
          </div>

          <motion.div style={{ flex: "0 0 340px", perspective: 900 }} className="hero-portrait-col"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.55, ease: EASE }}>
            <motion.div style={{ y: portraitY }}>
              <motion.div
                onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}
                style={{ rotateX, rotateY, transformStyle: "preserve-3d", background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 16, padding: 10, boxShadow: "var(--shadow-2)" }}
                data-testid="hero-portrait">
                <img src="/assets/portrait.webp" alt="Rajesh Vaishnav" className="hero-portrait-img"
                  style={{ display: "block", width: 320, height: 400, maxWidth: "100%", objectFit: "cover", borderRadius: 8 }} />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
